import { HttpErrorResponse } from '@angular/common/http';
import { AnnualReportMetaInformationDTO, PrintUsingPdfTronDto } from '@modeso/aumico-lib-annualreport-fe';
import {
    IAnnualReport,
    IFinancialStatementSource,
    IFinStatements,
    IGeneralInformation,
    ReportType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import Debug from 'debug';
import { cloneDeep } from 'lodash-es';
import { templateConstants } from '../../../../aumico-project-reports/src/app/shared/template-constants';
import * as fromAnnualReportActions from '../actions/annualReport.actions';
import * as fromArSharesActions from '../actions/annualreport-shares.action';
import { ANNUAL_REPORT_FEATURE_KEY } from '../constants/feature-state-key';
import { IAuditLogDTO } from '../dtos/get-audit-log.dto';
import { PlaceholderHelper } from '../helpers/placeholder.helper';
import { SortingItemsHelper } from '../helpers/sorting-items.helper';

const debug = Debug('aumico:aumico-lib-annualreport-fe:AumicoAnnualReportReducer');

export interface AppState {
    aumicoAnnualReportMicroservice: FeatureState;
}

export interface FeatureState extends EntityState<AnnualReportMetaInformationDTO> {
    financialStatementImportLoseFocus?: {};
    error: HttpErrorResponse;
    activeAnnualReport: IAnnualReport | null;
    activeAnnualReportEncryptedId: string;
    // previous annual report options for selection
    previousAnnualReportOptions: Partial<IAnnualReport>[] | null;
    // id of annual report for balance autofill
    annualReportForBalanceAutoFillId: string | null;
    // id of annual report for income autofill
    annualReportForIncomeAutoFillId: string | null;
    // annual report to take the balance autofill from
    annualReportForBalanceAutoFill: IAnnualReport | null;
    // annual report to take the income autofill from
    annualReportForIncomeAutoFill: IAnnualReport | null;
    // annual report to take (upload + mapping) autofill from
    annualReportForUploadAndMappingAutoFill: IAnnualReport | null;
    balanceFinancialStatementSource: IFinancialStatementSource;
    incomeFinancialStatementSource: IFinancialStatementSource;
    //Data For Print Preview and Pdf Tron Print
    printView: PrintUsingPdfTronDto;
    // Data of rich text placeholders and its current value
    placeholders: IDynamicKeyValue;
    // Annual report to copy notes structure from
    annualReportToCopyNotesStructureFrom: IAnnualReport | null;
    // active annual reports list for the dialog to copy upload and mapping or notes from
    annualReportsListToCopyFrom: AnnualReportMetaInformationDTO[];
    fcpIsActive: boolean;
    annualReportAuditLogs: IAuditLogDTO[];
}

export function selectAnnualReportEncryptedId(a: AnnualReportMetaInformationDTO): string {
    return a.annualReportEncryptedId;
}

export const adapter: EntityAdapter<AnnualReportMetaInformationDTO> =
    createEntityAdapter<AnnualReportMetaInformationDTO>({
        selectId: selectAnnualReportEncryptedId,
    });

export const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

export const selectFeature = createFeatureSelector<FeatureState>(ANNUAL_REPORT_FEATURE_KEY);

export const getActiveAnnualReport = (state: FeatureState) => state.activeAnnualReport;
export const getActiveAnnualReportEncryptedId = (state: FeatureState) => state.activeAnnualReportEncryptedId;

export const getAnnualReportForBalanceAutoFillId = (state: FeatureState) => state.annualReportForBalanceAutoFillId;
export const getAnnualReportForBalanceAutoFill = (state: FeatureState) => state.annualReportForBalanceAutoFill;

export const getAnnualReportForIncomeAutoFillId = (state: FeatureState) => state.annualReportForIncomeAutoFillId;
export const getAnnualReportForIncomeAutoFill = (state: FeatureState) => state.annualReportForIncomeAutoFill;

export const getAnnualReportsListToCopyFrom = (state: FeatureState) => state.annualReportsListToCopyFrom;

export const getAnnualReportForUploadAndMappingAutofill = (state: FeatureState) =>
    state.annualReportForUploadAndMappingAutoFill;

export const getError = (state: FeatureState) => state.error;

export const getBalanceFinancialStatementSource = (state: FeatureState) => state.balanceFinancialStatementSource;
export const getIncomeFinancialStatementSource = (state: FeatureState) => state.incomeFinancialStatementSource;

export const getFinancialStatementImportLoseFocus = (state: FeatureState) => state.financialStatementImportLoseFocus;

export const getPrintView = (state: FeatureState) => state.printView;

export const getAnnualReportToCopyNotesStructureFrom = (state: FeatureState) =>
    state.annualReportToCopyNotesStructureFrom;
export const getAnnualReportAuditLogs = (state: FeatureState) => state.annualReportAuditLogs;

export const getPreviousAnnualReportOptions = (state: FeatureState) => state.previousAnnualReportOptions;

export const selectAnnualReportEntities = createSelector(selectFeature, selectEntities);

export const selectError = createSelector(selectFeature, getError);

export const selectFinancialStatementImportLoseFocus = createSelector(
    selectFeature,
    getFinancialStatementImportLoseFocus,
);

export const selectFcpActivationState = createSelector(
    selectFeature,
    (state) => state?.fcpIsActive ?? false, // Default to false
);

export const selectAnnualReportForBalanceAutoFill = createSelector(selectFeature, getAnnualReportForBalanceAutoFill);

export const selectAnnualReportForIncomeAutoFill = createSelector(selectFeature, getAnnualReportForIncomeAutoFill);

export const selectBalanceFinancialStatementSource = createSelector(selectFeature, getBalanceFinancialStatementSource);

export const selectIncomeFinancialStatementSource = createSelector(selectFeature, getIncomeFinancialStatementSource);

export const selectAllAnnualReports = createSelector(selectFeature, selectAll);

export const selectActiveAnnualReport = createSelector(selectFeature, getActiveAnnualReport);

export const selectAnnualReportsListToCopyFrom = createSelector(selectFeature, getAnnualReportsListToCopyFrom);

export const selectActiveAnnualReportEncryptedId = createSelector(selectFeature, getActiveAnnualReportEncryptedId);

export const selectAnnualReportForBalanceAutoFillId = createSelector(
    selectFeature,
    getAnnualReportForBalanceAutoFillId,
);

export const selectAnnualReportForIncomeAutoFillId = createSelector(selectFeature, getAnnualReportForIncomeAutoFillId);

export const selectAnnualReportForUploadAndMappingAutoFill = createSelector(
    selectFeature,
    getAnnualReportForUploadAndMappingAutofill,
);

export const selectAnnualReportToCopyNotesStructureFrom = createSelector(
    selectFeature,
    getAnnualReportToCopyNotesStructureFrom,
);

export const selectPlaceholderMap = createSelector(selectFeature, (state: FeatureState) => {
    return state.placeholders;
});
export const selectPrintView = createSelector(selectFeature, getPrintView);

export const selectAnnualReportAuditLogs = createSelector(selectFeature, getAnnualReportAuditLogs);
export const selectPreviousAnnualReportOptions = createSelector(selectFeature, getPreviousAnnualReportOptions);
export const selectActiveMetaInformationEntity = createSelector(
    selectAnnualReportEntities,
    selectActiveAnnualReportEncryptedId,
    (entities, annualReportEncryptedId) => entities[annualReportEncryptedId],
);
export const selectActiveAnnualReportAccessibility = createSelector(
    selectActiveMetaInformationEntity,
    (metaInformation) => metaInformation?.isSealed,
);
export const selectActiveMetaInformationById = (id: string) =>
    createSelector(selectAnnualReportEntities, (entities) => entities[id]);

export const selectAnnualReportState = createSelector(selectFeature, (state: FeatureState) => state.activeAnnualReport);

export const isAnnualReportUrlIdValid = (id: string) =>
    createSelector(selectFeature, (state: FeatureState) => {
        const currentAnnualReportId = state.activeAnnualReport._id;
        return id === currentAnnualReportId;
    });

// we have to use the .finStatemnts from the activeAnnualReport because we are creating the selector
export const finStatmentsOriginal = createSelector(
    selectActiveAnnualReport,
    (activeAnnualReport) => activeAnnualReport?.finStatements ?? [],
);

export const finStatementsForBalanceAutoFill = createSelector(
    selectAnnualReportForBalanceAutoFill,
    (annualReport) => annualReport?.finStatements ?? [],
);

export const finStatementsForIncomeAutoFill = createSelector(
    selectAnnualReportForIncomeAutoFill,
    (annualReport) => annualReport?.finStatements ?? [],
);

export const groupingMaskingForBalanceAutoFill = createSelector(
    selectAnnualReportForBalanceAutoFill,
    (annualReport) => annualReport?.groupingAndMasking ?? null,
);

export const groupingMaskingForIncomeAutoFill = createSelector(
    selectAnnualReportForIncomeAutoFill,
    (annualReport) => annualReport?.groupingAndMasking ?? null,
);

export const groupingMaskingForCopy = createSelector(
    selectAnnualReportForUploadAndMappingAutoFill,
    (annualReport) => annualReport?.groupingAndMasking ?? null,
);

export const finStatmentsOriginalSelectedYearForUploadAndMappingAutofill = createSelector(
    selectAnnualReportForUploadAndMappingAutoFill,
    (selectedAnnualReport) => selectedAnnualReport?.finStatements ?? [],
);

const selectOutputFinStatements = createSelector(
    selectFcpActivationState,
    selectActiveAnnualReport,
    (fcpActivationState, activeAnnualReport) => {
        // If `fcpActivationState` is false or `outputFinStatements` doesn't exist, return an empty array
        if (!fcpActivationState || !activeAnnualReport?.fcp?.outputFinStatements) {
            return [];
        }
        // Otherwise, return `outputFinStatements` from the activeAnnualReport's fcp
        return activeAnnualReport.fcp.outputFinStatements;
    },
);

const selectOutputFinStatementsOfBalanceAutofill = createSelector(
    selectFcpActivationState,
    selectAnnualReportForBalanceAutoFill,
    (fcpActivationState, annualReport) => {
        // If `selectFcpActivationState` is false, return an empty array
        if (!fcpActivationState) {
            return [];
        }
        // Otherwise, return `outputFinStatements`
        return annualReport?.fcp?.outputFinStatements ?? [];
    },
);

const selectOutputFinStatementsOfIncomeAutofill = createSelector(
    selectFcpActivationState,
    selectAnnualReportForIncomeAutoFill,
    (fcpActivationState, annualReport) => {
        // If `selectFcpActivationState` is false, return an empty array
        if (!fcpActivationState) {
            return [];
        }
        // Otherwise, return `outputFinStatements`
        return annualReport?.fcp?.outputFinStatements ?? [];
    },
);

const selectOutputFinStatementsOfSelectedYearForUploadAndMappingAutofill = createSelector(
    selectFcpActivationState,
    selectAnnualReportForUploadAndMappingAutoFill,
    (fcpActivationState, selectedAnnualReport) => {
        // If `selectFcpActivationState` is false, return an empty array
        if (!fcpActivationState) {
            return [];
        }
        // Otherwise, return `outputFinStatements`
        return selectedAnnualReport?.fcp?.outputFinStatements ?? [];
    },
);

export const finStatmentsAdjusted = createSelector(
    selectOutputFinStatements,
    finStatmentsOriginal,
    (outputFinStatements, finStatmentsOriginal) => {
        return outputFinStatements && outputFinStatements.length > 0 ? outputFinStatements : finStatmentsOriginal;
    },
);
export const finStatmentsAdjustedOfBalanceAutofill = createSelector(
    selectOutputFinStatementsOfBalanceAutofill,
    finStatementsForBalanceAutoFill,
    (outputFinStatements, finStatmentsOriginal) => {
        return outputFinStatements && outputFinStatements.length > 0 ? outputFinStatements : finStatmentsOriginal;
    },
);

export const finStatmentsAdjustedOfIncomeAutofill = createSelector(
    selectOutputFinStatementsOfIncomeAutofill,
    finStatementsForIncomeAutoFill,
    (outputFinStatements, finStatmentsOriginal) => {
        return outputFinStatements && outputFinStatements.length > 0 ? outputFinStatements : finStatmentsOriginal;
    },
);

// autofilling from 2 different annual reports one for balance and one for income
export const finStatmentsAdjustedForCombinedAutofill = createSelector(
    selectActiveAnnualReport,
    selectOutputFinStatementsOfBalanceAutofill,
    selectOutputFinStatementsOfIncomeAutofill,
    finStatementsForBalanceAutoFill,
    finStatementsForIncomeAutoFill,
    (
        activeAnnualReport,
        outputFinStatementsBalance,
        outputFinStatementsIncome,
        finStatmentsOriginalBalanceAutofill,
        finStatmentsOriginalIncomeAutofill,
    ) => {
        if (activeAnnualReport == null) {
            return [];
        }
        const outputFinStatements: IFinStatements[] = [];
        if (activeAnnualReport.generalInformation?.isPreviousDataTakenFromPreviousAnnualReport === true) {
            outputFinStatements[templateConstants.balanceTemplate] =
                outputFinStatementsBalance && outputFinStatementsBalance.length > 0
                    ? outputFinStatementsBalance[templateConstants.balanceTemplate]
                    : finStatmentsOriginalBalanceAutofill[templateConstants.balanceTemplate];
        } else if (activeAnnualReport.generalInformation?.isPreviousDataTakenFromPreviousAnnualReport === false) {
            outputFinStatements[templateConstants.balanceTemplate] =
                outputFinStatementsBalance && outputFinStatementsBalance.length > 0
                    ? outputFinStatementsBalance[templateConstants.balanceTemplate]
                    : undefined;
        }

        if (
            activeAnnualReport.generalInformation?.isPreviousDataTakenFromPreviousAnnualReportForIncomeStatement ===
            true
        ) {
            outputFinStatements[templateConstants.incomeTemplate] =
                outputFinStatementsIncome && outputFinStatementsIncome.length > 0
                    ? outputFinStatementsIncome[templateConstants.incomeTemplate]
                    : finStatmentsOriginalIncomeAutofill[templateConstants.incomeTemplate];
        } else if (
            activeAnnualReport.generalInformation?.isPreviousDataTakenFromPreviousAnnualReportForIncomeStatement ===
            false
        ) {
            outputFinStatements[templateConstants.incomeTemplate] =
                outputFinStatementsIncome && outputFinStatementsIncome.length > 0
                    ? outputFinStatementsIncome[templateConstants.incomeTemplate]
                    : undefined;
        } else if (
            activeAnnualReport.generalInformation?.isPreviousDataTakenFromPreviousAnnualReportForIncomeStatement == null
        ) {
            // for older reports where this property was not available we take the balance autofill value for income statement
            outputFinStatements[templateConstants.incomeTemplate] =
                outputFinStatementsBalance && outputFinStatementsBalance.length > 0
                    ? outputFinStatementsBalance[templateConstants.incomeTemplate]
                    : finStatmentsOriginalBalanceAutofill[templateConstants.incomeTemplate];
        }
        return outputFinStatements;
    },
);

export const getAnnualProfitOfBalanceAutofillFinstatement = (balanceSheetDate: string) =>
    createSelector(selectAnnualReportForBalanceAutoFill, (annualReport) => {
        if (!annualReport || !annualReport.finStatements || annualReport.finStatements.length === 0) {
            return null;
        }

        if (
            annualReport.finStatements[templateConstants.incomeTemplate] &&
            annualReport.finStatements[templateConstants.incomeTemplate].templateAndAccountsMapping &&
            annualReport.finStatements[templateConstants.incomeTemplate].templateAndAccountsMapping.template &&
            annualReport.finStatements[templateConstants.incomeTemplate].templateAndAccountsMapping.template.group
        ) {
            if (annualReport.generalInformation?.incomePreviousYearToDate?.toString() === balanceSheetDate.toString()) {
                return annualReport.finStatements[templateConstants.incomeTemplate].templateAndAccountsMapping.template.group.calculatedOpeningBalance;
            }

            if (annualReport.generalInformation?.incomeCurrentYearToDate?.toString() === balanceSheetDate.toString()) {
                return annualReport.finStatements[templateConstants.incomeTemplate].templateAndAccountsMapping.template.group.calculatedClosingBalance;
            }
        }
        return null;
    });

export const finStatmentsAdjustedOfSelectedYearForUploadAndMappingAutofill = createSelector(
    selectOutputFinStatementsOfSelectedYearForUploadAndMappingAutofill,
    finStatmentsOriginalSelectedYearForUploadAndMappingAutofill,
    (outputFinStatements, finStatmentsOriginal) => {
        return outputFinStatements && outputFinStatements.length > 0 ? outputFinStatements : finStatmentsOriginal;
    },
);

export const selectActiveGeneralInformation = createSelector(
    selectActiveAnnualReport,
    (activeAnnualReport) => activeAnnualReport?.generalInformation ?? null,
);

export const selectActiveDueDates = createSelector(
    selectActiveGeneralInformation,
    (generalInformation: IGeneralInformation | null) => {
        if (!generalInformation) {
            return null;
        }

        return {
            dueDate: new Date(generalInformation.balanceSheetDate),
            dueDatePY: generalInformation.noPreviousData ? null : new Date(generalInformation.balancePreviousYearDate),
            periodFrom: new Date(generalInformation.incomeCurrentYearFromDate),
            periodTo: new Date(generalInformation.incomeCurrentYearToDate),
            periodFromPY: generalInformation.noPreviousData ? null : new Date(generalInformation.incomePreviousYearFromDate),
            periodToPY: generalInformation.noPreviousData ? null : new Date(generalInformation.incomePreviousYearToDate),
        };
    },
);

export const selectActiveOriginalFinStatementsFromActiveAnnualReport = createSelector(
    selectActiveAnnualReport,
    (activeAnnualReport) => activeAnnualReport?.finStatements ?? [],
);

export const selectFcpInputFinStatementsFromActiveAnnualReport = createSelector(
    selectActiveAnnualReport,
    (activeAnnualReport) => activeAnnualReport?.fcp?.inputFinStatements ?? [],
);

export const selectActiveFinStatementsFromActiveAnnualReport = createSelector(
    selectOutputFinStatements,
    selectActiveOriginalFinStatementsFromActiveAnnualReport,
    (outputFinStatements, originalFinStatements) => {
        return outputFinStatements && outputFinStatements.length > 0 ? outputFinStatements : originalFinStatements;
    },
);

export const initialState: FeatureState = adapter.getInitialState({
    activeAnnualReport: null,
    activeAnnualReportEncryptedId: null,
    error: null,
    annualReportForBalanceAutoFill: null,
    annualReportForIncomeAutoFill: null,
    previousAnnualReportOptions: [],
    annualReportForBalanceAutoFillId: null,
    annualReportForIncomeAutoFillId: null,
    annualReportForUploadAndMappingAutoFill: null,
    annualReportForUploadAndMappingAutoFillId: null,
    annualReportToCopyNotesStructureFrom: null,
    balanceFinancialStatementSource: null,
    incomeFinancialStatementSource: null,
    selectedMetaInformationId: null,
    printView: null,
    placeholders: {},
    annualReportsListToCopyFrom: [],
    fcpIsActive: false,
    annualReportAuditLogs: [],
});

const prepareSortedAnnualReportForState = (annualReport: IAnnualReport | null): IAnnualReport | null => {
    if (annualReport == null) {
        return null;
    }

    // Reducer-level guard keeps sorting helper contract non-null and normalizes report type.
    const preparedReport = SortingItemsHelper.prepareSortedItemsInAnnualReport(annualReport);
    if (preparedReport === null) {
        return null;
    }

    if (preparedReport.setupData != null && preparedReport.setupData.reportType == null) {
        preparedReport.setupData.reportType = ReportType.ANNUAL;
    }

    return preparedReport;
};

export const annualReportReducer = createReducer(
    initialState,
    on(fromAnnualReportActions.createAnnualReport, (state) => ({
        ...state,
        error: undefined,
        annualReportForBalanceAutoFill: null,
        annualReportForIncomeAutoFill: null,
        annualReportForUploadAndMappingAutoFill: null,
        annualReportToCopyNotesStructureFrom: null,
    })),
    on(fromAnnualReportActions.onCreateAnnualReportSuccessfully, (state, action) => {
        const newlyAddedMetaInformation = {
            _id: action.payload.annualReportMetaInformationId,
            annualReportEncryptedId: action.payload.annualReportEncryptedId,
        } as AnnualReportMetaInformationDTO;

        return adapter.addOne(newlyAddedMetaInformation, {
            ...state,
            activeAnnualReport: action.payload.annualReport,
            activeAnnualReportEncryptedId: action.payload.annualReportEncryptedId,
            selectedMetaInformationId: action.payload.annualReportMetaInformationId,
        });
    }),

    on(fromAnnualReportActions.onCreateAnnualReportFailed, (state, action) => ({
        ...state,
        activeAnnualReport: undefined,
        error: action.payload,
    })),

    on(fromAnnualReportActions.updateAnnualReport, (state) => ({ ...state, error: undefined })),
    on(fromAnnualReportActions.enforceStateUpdateForAnnualReport, (state, action) => {
        // This will enforce a state update without calling the backend
        const report = prepareSortedAnnualReportForState(action.payload.annualReport);
        return {
            ...state,
            activeAnnualReport: report,
        };
    }),

    on(fromAnnualReportActions.onUpdateAnnualReportSuccessfully, (state, action) => {

        return {
            ...state,
        };
    }),
    on(fromAnnualReportActions.onUpdateAnnualReportFailed, (state, action) => ({
        ...state,
        activeAnnualReport: undefined,
        error: action.payload,
    })),

    on(fromAnnualReportActions.getAnnualReportForMappingAutoFill, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromAnnualReportActions.onGetAnnualReportForMappingAutoFillSuccessfully, (state: FeatureState, action) => {
        if (action.payload == null) {
            return {
                ...state,
                annualReportForMappingAutoFill: null,
            };
        }
        const annualReport = cloneDeep(action.payload);
        // TODO: Attention we need to check if this should really modify the ar.finstatements or fcp.outputFinStatements
        const preparedReport = prepareSortedAnnualReportForState(annualReport);
        return {
            ...state,
            annualReportForUploadAndMappingAutoFill: preparedReport,
            error: undefined,
        };
    }),
    on(fromAnnualReportActions.onGetAnnualReportForMappingAutoFillFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromAnnualReportActions.getActiveAnnualReport, (state: FeatureState, action) => ({
        ...state,
        error: undefined,
    })),
    on(fromAnnualReportActions.onGetActiveAnnualReportSuccessfully, (state: FeatureState, action) => {
        const annualReport = cloneDeep(action.payload);
        // TODO: Attention we need to check if this should really modify the ar.finstatements or fcp.outputFinStatements
        const preparedReport = prepareSortedAnnualReportForState(annualReport);
        const id = state.activeAnnualReportEncryptedId;
        const stateWithIntegrations =
            id != null && state.entities[id] != null && action.payload.integrations != null
                ? adapter.updateOne({ id, changes: { integrations: action.payload.integrations } }, state)
                : state;
        return {
            ...stateWithIntegrations,
            activeAnnualReport: preparedReport,
        };
    }),
    on(fromAnnualReportActions.onGetActiveAnnualReportFailed, (state: FeatureState, action) => {
        return {
            ...state,
            activeAnnualReport: undefined,
            activeAnnualReportEncryptedId: undefined,
            placeholders: PlaceholderHelper.resetPlaceHolderOfAnnualReport(cloneDeep(state.placeholders)),
            error: action.payload,
        };
    }),

    on(fromAnnualReportActions.getPreviousAnnualReportOptions, (state: FeatureState) => ({
        ...state,
        error: undefined,
    })),
    on(fromAnnualReportActions.onGetPreviousAnnualReportOptionsSuccessfully, (state: FeatureState, action) => ({
        ...state,
        previousAnnualReportOptions: action.payload,
        error: undefined,
    })),
    on(fromAnnualReportActions.onGetPreviousAnnualReportOptionsFailed, (state: FeatureState, action) => ({
        ...state,
        previousAnnualReportOptions: null,
        error: action.payload,
    })),

    on(fromAnnualReportActions.getBalanceAutofill, (state: FeatureState, action) => ({
        ...state,
        error: undefined,
    })),
    on(fromAnnualReportActions.onGetBalanceAutofillSuccessfully, (state: FeatureState, action) => {
        // TODO: Attention we need to check if this should really modify the ar.finstatements or fcp.outputFinStatements
        const report = prepareSortedAnnualReportForState(action.payload);
        return {
            ...state,
            annualReportForBalanceAutoFill: report,
        };
    }),
    on(fromAnnualReportActions.onGetBalanceAutofillFailed, (state: FeatureState, action) => ({
        ...state,
        annualReportForBalanceAutoFill: undefined,
        error: action.payload,
    })),

    on(fromAnnualReportActions.getIncomeAutofill, (state: FeatureState, action) => ({
        ...state,
        error: undefined,
    })),
    on(fromAnnualReportActions.onGetIncomeAutofillSuccessfully, (state: FeatureState, action) => {
        // TODO: Attention we need to check if this should really modify the ar.finstatements or fcp.outputFinStatements
        const report = prepareSortedAnnualReportForState(action.payload);
        return {
            ...state,
            annualReportForIncomeAutoFill: report,
        };
    }),
    on(fromAnnualReportActions.onGetIncomeAutofillFailed, (state: FeatureState, action) => ({
        ...state,
        annualReportForIncomeAutoFill: undefined,
        error: action.payload,
    })),

    on(fromAnnualReportActions.setBalanceAutoFillIdWithChosenAnnualReportId, (state: FeatureState, action) => ({
        ...state,
        annualReportForBalanceAutoFillId: action.payload,
    })),

    on(fromAnnualReportActions.resetBalanceAutoFillId, (state: FeatureState, action) => ({
        ...state,
        annualReportForBalanceAutoFillId: null,
    })),

    on(fromAnnualReportActions.resetAnnualReportForBalanceAutoFill, (state: FeatureState, action) => ({
        ...state,
        annualReportForBalanceAutoFill: null,
    })),

    on(fromAnnualReportActions.setIncomeAutoFillIdWithChosenAnnualReportId, (state: FeatureState, action) => ({
        ...state,
        annualReportForIncomeAutoFillId: action.payload,
    })),

    on(fromAnnualReportActions.resetIncomeAutoFillId, (state: FeatureState, action) => ({
        ...state,
        annualReportForIncomeAutoFillId: null,
    })),

    on(fromAnnualReportActions.resetAnnualReportForIncomeAutoFill, (state: FeatureState, action) => ({
        ...state,
        annualReportForIncomeAutoFill: null,
    })),

    on(fromAnnualReportActions.getActiveAnnualReports, (state) => ({
        ...state,
        error: undefined,
        annualReportForAutoFill: null,
    })),
    on(fromAnnualReportActions.onGettingActiveAnnualReportsSuccessfully, (state, action) => {
        return adapter.setAll(action.payload, { ...state, activeAnnualReport: undefined });
    }),
    on(fromAnnualReportActions.onGettingActiveAnnualReportsFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromAnnualReportActions.getAnnualReportsListToCopyFrom.getAnnualReportsListToCopyFrom, (state) => ({
        ...state,
        error: undefined,
    })),
    on(
        fromAnnualReportActions.getAnnualReportsListToCopyFrom.getAnnualReportsListToCopyFromSuccess,
        (state, action) => ({
            ...state,
            annualReportsListToCopyFrom: action.payload,
        }),
    ),
    on(
        fromAnnualReportActions.getAnnualReportsListToCopyFrom.getAnnualReportsListToCopyFromFailed,
        (state, action) => ({
            ...state,
            error: action.payload,
        }),
    ),

    on(fromAnnualReportActions.getArchivedAnnualReports, (state) => ({
        ...state,
        error: undefined,
        annualReportForAutoFill: null,
    })),
    on(fromAnnualReportActions.onGettingArchivedAnnualReportsSuccessfully, (state, action) => {
        return adapter.setAll(action.payload, { ...state, activeAnnualReport: undefined });
    }),
    on(fromAnnualReportActions.onGettingArchivedAnnualReportsFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromAnnualReportActions.setActiveAnnualReport, (state: FeatureState, action) => {
        // TODO: Attention we need to check if this should really modify the ar.finstatements or fcp.outputFinStatements
        const report = prepareSortedAnnualReportForState(action.payload);
        return {
            ...state,
            activeAnnualReport: report,
        };
    }),

    on(fromAnnualReportActions.setActiveAnnualReportEncryptedId, (state: FeatureState, action) => ({
        ...state,
        activeAnnualReportEncryptedId: action.payload,
    })),

    on(fromAnnualReportActions.setBalanceFinancialStatementSource, (state: FeatureState, action) => ({
        ...state,
        balanceFinancialStatementSource: { ...action.payload },
    })),

    on(fromAnnualReportActions.setIncomeFinancialStatementSource, (state: FeatureState, action) => ({
        ...state,
        incomeFinancialStatementSource: { ...action.payload },
    })),

    on(fromAnnualReportActions.archiveAnnualReports, (state) => ({ ...state, error: undefined })),
    on(fromAnnualReportActions.onArchiveAnnualReportsSuccessfully, (state, action) => {
        // Remove archived annual reports from active (current) list
        return adapter.removeMany(action.annualReportIds, { ...state });
    }),
    on(fromAnnualReportActions.onArchiveAnnualReportsFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromAnnualReportActions.restoreAnnualReports, (state) => ({ ...state, error: undefined })),
    on(fromAnnualReportActions.onRestoreAnnualReportsSuccessfully, (state, action) => {
        // Remove restored annual reports from archived (current) list
        return adapter.removeMany(action.annualReportIds, { ...state });
    }),
    on(fromAnnualReportActions.onRestoreAnnualReportsFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromAnnualReportActions.deleteAnnualReports, (state) => ({ ...state, error: undefined })),
    on(fromAnnualReportActions.onDeleteAnnualReportsSuccessfully, (state, action) => {
        return adapter.removeMany(action.annualReportIds, { ...state });
    }),
    on(fromAnnualReportActions.onDeleteAnnualReportsFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    /** reset all editing data */
    on(fromAnnualReportActions.flush, (state: FeatureState, action) => ({
        ...state,
        activeAnnualReport: null,
        activeAnnualReportEncryptedId: null,
        error: null,
        previousAnnualReportOptions: [],
        annualReportForBalanceAutoFill: null,
        annualReportForBalanceAutoFillId: null,
        annualReportForIncomeAutoFill: null,
        annualReportForIncomeAutoFillId: null,
        annualReportForUploadAndMappingAutoFillId: null,
        annualReportForUploadAndMappingAutoFill: null,
        annualReportToCopyNotesStructureFrom: null,
        balanceFinancialStatementSource: null,
        incomeFinancialStatementSource: null,
        accounts: null,
        printView: null,
        annualReportsListToCopyFrom: [],
        annualReportAuditLogs: [],
    })),
    /** reset all editing data */
    on(fromAnnualReportActions.removeActiveAnnualReport, (state: FeatureState, action) => ({
        ...state,
        activeAnnualReport: null,
        activeAnnualReportEncryptedId: null,
        error: null,
        previousAnnualReportOptions: [],
        annualReportForBalanceAutoFill: null,
        annualReportForBalanceAutoFillId: null,
        annualReportForIncomeAutoFill: null,
        annualReportForIncomeAutoFillId: null,
        annualReportForUploadAndMappingAutoFill: null,
        annualReportForUploadAndMappingAutoFillId: null,
        annualReportToCopyNotesStructureFrom: null,
        balanceFinancialStatementSource: null,
        incomeFinancialStatementSource: null,
        accounts: null,
        printView: null,
        annualReportsListToCopyFrom: [],
        annualReportAuditLogs: [],
    })),
    on(fromAnnualReportActions.triggerFinancialStatementImportLoseFocus, (state: FeatureState, action) => ({
        ...state,
        financialStatementImportLoseFocus: {},
    })),

    on(fromAnnualReportActions.resetAnnualReports, (state) => {
        return adapter.removeAll({
            ...state,
            activeAnnualReport: null,
            activeAnnualReportEncryptedId: null,
            error: null,
            previousAnnualReportOptions: [],
            annualReportForBalanceAutoFillId: null,
            annualReportForIncomeAutoFillId: null,
            annualReportForBalanceAutoFill: null,
            annualReportForIncomeAutoFill: null,
            annualReportForUploadAndMappingAutoFill: null,
            annualReportToCopyNotesStructureFrom: null,
            balanceFinancialStatementSource: null,
            incomeFinancialStatementSource: null,
            printView: null,
            annualReportsListToCopyFrom: [],
            annualReportAuditLogs: [],
        });
    }),

    on(fromAnnualReportActions.exportAnnualReportExcel.exportAnnualReportExcel, (state) => state),
    on(fromAnnualReportActions.exportAnnualReportExcel.exportAnnualReportExcelSuccess, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromAnnualReportActions.exportAnnualReportExcel.exportAnnualReportExcelFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromArSharesActions.getShareInformation.success, (state, action) => {
        return adapter.updateOne(
            {
                id: state.activeAnnualReportEncryptedId,
                changes: {
                    shareInformation: action.payload.shareInformation,
                },
            },
            { ...state, error: null },
        );
    }),

    on(fromAnnualReportActions.exportAnnualReportToWord.exportAnnualReportToWord, (state) => ({
        ...state,
        error: undefined,
    })),
    on(
        (fromAnnualReportActions.exportAnnualReportToWord.exportAnnualReportToWordSuccess,
            (state) => ({ ...state, error: undefined })),
    ),
    on(fromAnnualReportActions.exportAnnualReportToWord.exportAnnualReportToWordFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),
    on(fromAnnualReportActions.setPrintView, (state, action) => ({
        ...state,
        printView: action.payload,
    })),

    on(fromAnnualReportActions.resetActiveAnnualReport, (state: FeatureState, action) => ({
        ...state,
        activeAnnualReport: null,
    })),

    on(fromAnnualReportActions.updateUserPlaceholderData, (state: FeatureState, action) => {
        const updatedPlaceHolders = PlaceholderHelper.setPlaceholderForUser(
            cloneDeep(state.placeholders),
            action.userName,
        );

        return {
            ...state,
            placeholders: { ...updatedPlaceHolders },
        };
    }),

    on(fromAnnualReportActions.updateOrganizationPlaceholderData, (state: FeatureState, action) => {
        const updatedPlaceHolders = PlaceholderHelper.setPlaceholderFoOrganization(
            cloneDeep(state.placeholders),
            action.organization,
        );

        return {
            ...state,
            placeholders: { ...updatedPlaceHolders },
        };
    }),

    on(fromAnnualReportActions.updateAopSnapshotPlaceholder, (state: FeatureState, action) => {
        const updatedPlaceHolders = PlaceholderHelper.setAopSnapshotPlaceholder(state.placeholders, action.aopSnapshot);

        return {
            ...state,
            placeholders: { ...updatedPlaceHolders },
        };
    }),

    on(fromAnnualReportActions.updateAopSnapshotCurrentYearPlaceholder, (state: FeatureState, action) => {
        const updatedPlaceHolders = PlaceholderHelper.setAopSnapshotCurrentYearPlaceholder(
            state.placeholders,
            action.aopSnapshotCurrentYear,
        );

        return {
            ...state,
            placeholders: { ...updatedPlaceHolders },
        };
    }),

    on(fromAnnualReportActions.updateAnnualReportPlaceHolder, (state: FeatureState, action) => {
        return {
            ...state,
            placeholders: { ...state.placeholders, ...action.payload },
        };
    }),
    on(fromAnnualReportActions.resetFcp, (state: FeatureState) => {
        const annualReportWithNoFcp = cloneDeep(state.activeAnnualReport);
        annualReportWithNoFcp.fcp = null;
        return {
            ...state,
            activeAnnualReport: annualReportWithNoFcp,
        };
    }),
    on(fromAnnualReportActions.updateFcpState, (state, action) => ({
        ...state,
        fcpIsActive: action.payload,
    })),
    on(fromAnnualReportActions.getAnnualReportToCopyNotesStructureFrom, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromAnnualReportActions.onGetAnnualReportToCopyNotesStructureFromSuccessfully, (state: FeatureState, action) => {
        const annualReport = cloneDeep(action.payload);
        const preparedReport = prepareSortedAnnualReportForState(annualReport);
        return {
            ...state,
            annualReportToCopyNotesStructureFrom: preparedReport,
            error: undefined,
        };
    }),
    on(fromAnnualReportActions.onGetAnnualReportToCopyNotesStructureFromFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    // Seal Annual Report Reducer
    on(fromAnnualReportActions.sealAnnualReport, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromAnnualReportActions.onSealAnnualReportSuccessfully, (state: FeatureState, action) => {
        if (state.activeAnnualReportEncryptedId) {
            return adapter.updateOne(
                {
                    id: state.activeAnnualReportEncryptedId,
                    changes: {
                        isSealed: true,
                    },
                },
                {
                    ...state,
                    activeAnnualReport: { ...state.activeAnnualReport, isSealed: true },
                    error: undefined,
                },
            );
        }

        return {
            ...state,
            activeAnnualReport: { ...state.activeAnnualReport, isSealed: true },
            error: undefined,
        };
    }),
    on(fromAnnualReportActions.onSealAnnualReportFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    // Unseal Annual Report Reducer
    on(fromAnnualReportActions.unsealAnnualReport, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromAnnualReportActions.onUnsealAnnualReportSuccessfully, (state: FeatureState, action) => {
        if (state.activeAnnualReportEncryptedId) {
            return adapter.updateOne(
                {
                    id: state.activeAnnualReportEncryptedId,
                    changes: {
                        isSealed: false,
                    },
                },
                {
                    ...state,
                    activeAnnualReport: { ...state.activeAnnualReport, isSealed: false },
                    error: undefined,
                },
            );
        }

        return {
            ...state,
            activeAnnualReport: { ...state.activeAnnualReport, isSealed: false },

            error: undefined,
        };
    }),
    on(fromAnnualReportActions.onUnsealAnnualReportFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    // Audit Logs Reducer
    on(fromAnnualReportActions.getAnnualReportSealHistory, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromAnnualReportActions.onGetAnnualReportSealHistorySuccessfully, (state: FeatureState, action) => ({
        ...state,
        annualReportAuditLogs: action.payload,
        error: undefined,
    })),
    on(fromAnnualReportActions.onGetAnnualReportSealHistoryFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),
    on(fromAnnualReportActions.patchActiveAnnualReportIntegrations, (state: FeatureState, action) => {
        const id = state.activeAnnualReportEncryptedId;
        if (id == null || state.entities[id] == null) return state;
        return adapter.updateOne({ id, changes: { integrations: action.integrations } }, state);
    }),
    on(
        fromAnnualReportActions.setAnnualReportMetaInfo,
        (state: FeatureState, { annualReportMetaInfo, annualReport }) => {
            const preparedReport = prepareSortedAnnualReportForState(cloneDeep(annualReport));
            if (preparedReport == null) {
                return {
                    ...state,
                    activeAnnualReport: null,
                    activeAnnualReportEncryptedId: annualReportMetaInfo.annualReportEncryptedId,
                    annualReportForBalanceAutoFillId: null,
                    annualReportForIncomeAutoFillId: null,
                };
            }
            return {
                ...state,
                activeAnnualReport: preparedReport,
                activeAnnualReportEncryptedId: annualReportMetaInfo.annualReportEncryptedId,
                annualReportForBalanceAutoFillId: preparedReport.autoFillAnnualReportId,
                annualReportForIncomeAutoFillId: preparedReport.autoFillAnnualReportIdForIncomeStatement,
            };
        },
    ),
);

export function reducer(state: FeatureState | undefined, action: Action) {
    return annualReportReducer(state, action);
}
